import {
    AppLayout,
    Box,
    ContentLayout,
    Flashbar,
    Grid,
    Header,
    SpaceBetween,
    Spinner,
    StatusIndicator,
    Tabs,
} from '@amzn/awsui-components-react';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Navigation } from '../../components';
import {
    getBreadcrumbItemsFromCourse,
    calculateActualDuration,
    getNodeEnvironment,
    convertLearningActivityToCourse,
    RegistrationStatus,
    CONTENT_TYPE_CLOUD_QUEST,
    CLASS_CONTENT_TAB_ID,
    CourseDetailLocationState,
} from '../../common';
import './Course.scss';
import { CourseProps } from '../../common';
import { NotificationContext, DeliveryDetails, DurationIcon, LevelIcon, CategoryIcon, Breadcrumbs } from '../../components';
import { IltLearnerAssistantLayout } from '../../components/AIChatbot/IltLearnerAssistantLayout';
import { CourseDetails } from './CourseDetails';
import { CourseOutline } from './CourseOutline';
import { CloudQuestTab } from './CloudQuestTab';
import { PremiumContentTab } from './PremiumContentTab';
import { PAGE_NOT_FOUND_ROUTE } from '../../common';
import { CourseContext } from '../../components/course/CourseContext';
import { CourseBadges } from './Badges';
import { FlagNames, isFeatureEnabled, Stages } from '@amzn/aws-tc-ilt-common';
import { useLearningActivity } from '../../hooks/learningActivity/useLearningActivity';
import { useAuthContext } from '../../components/auth/AuthContext';
import { TelemetryEvents, TelemetryPage, TELEMETRY_EVENT_TYPES } from '../../common/TelemetryEvents';
import { useTrackUserEvent } from '../../common/telemetry';
import { SuspenseLiveComponent } from '../../components/LiveComponent/SuspenseLiveComponent';

/** Long enough that a merely slow remote still wins the race, short enough not to read as hung. */
export const CLASS_CONTENT_REPORT_TIMEOUT_MS = 10_000;

export const Course = () => {
    const [searchParams] = useSearchParams();
    const { id } = useParams<{ id: string }>();
    const [course, setCourse] = useState<CourseProps>();
    const [hasError, setHasError] = useState(false);
    const { courses, loading, refetchCourses } = useContext(CourseContext);
    const navigate = useNavigate();
    const { user } = useAuthContext();
    const { showErrorFlashbar } = useContext(NotificationContext);
    const orgId = searchParams.get('orgId');

    /**
     * Which tab is open. Seeded from navigation state so the page you came from can preselect one —
     * the dashboard's Class Content button opens the Class content tab.
     */
    const location = useLocation();
    const requestedTabId = (location.state as CourseDetailLocationState | null)?.activeTabId;
    const [activeTabId, setActiveTabId] = useState<string | undefined>(requestedTabId);

    /**
     * Whether this class has anything for the Class content tab. Only the XPS can answer: GRIMSBY's
     * `classroom_training_content` is a course-level tag that cannot express "this classroom has
     * nothing added yet".
     *
     * `undefined` means unanswered, and is distinct from `false`: starting at `false` would claim the
     * class is guides-only, showing a Student guide in the header before anything had checked.
     *
     * Every failure path resolves to `false` — failing closed costs a class that does have labs its
     * only route to them whenever the XPS is unavailable.
     */
    const [hasClassContent, setHasClassContent] = useState<boolean | undefined>(undefined);

    /**
     * Coerces anything but `true` to `false`, because props crossing the module-federation boundary
     * are not type-checked at runtime and a missing answer must not be mistaken for a pending one.
     *
     * `useCallback` is what makes that coercion safe to add: `onHasTabContent` is a dependency of the
     * reporting effect inside the remote, so an arrow rebuilt each render would re-run it each render.
     */
    const reportHasClassContent = useCallback((value: boolean) => setHasClassContent(value === true), []);

    const trackUserEvent = useTrackUserEvent();

    const isChatbotEnabled = isFeatureEnabled(FlagNames.MYCLASS_AI_CHATBOT, getNodeEnvironment() as Stages);
    const isBoltOnCloudQuestEnabled = isFeatureEnabled(FlagNames.BOLT_ON_CLOUD_QUEST, getNodeEnvironment() as Stages);
    const isIltClassroomsXpsEnabled = isFeatureEnabled(FlagNames.MYCLASS_ILT_CLASSROOMS_XPS, getNodeEnvironment() as Stages);

    const {
        data: learningActivityData,
        loading: learningActivityLoading,
        error: learningActivityError,
    } = useLearningActivity(
        id ?? '',
        user,
        {
            isTeamAuthorizationEnabled: true,
            hideJoinButtonForNonWebExCourse: true,
            privateLinkSharing: true,
            adcLinkSharing: true,
        },
        orgId ?? undefined,
    );

    // Determine if this is a Bolt-on Cloud Quest class from real LA data
    const contentConfigs = learningActivityData?.learningActivity?.content_configs;
    const cqConfig = contentConfigs?.find((c: { content_type: string }) => c.content_type === CONTENT_TYPE_CLOUD_QUEST);
    const isBoltOnClass = !!isBoltOnCloudQuestEnabled && !!cqConfig;

    // Extract CQ catalog item ID from content_configs.related_catalog_item
    const cqCatalogItemId = cqConfig?.related_catalog_item?.id;

    const accessEndTimestamp = cqConfig?.access_end_timestamp;

    /**
     * Every arrival at this page starts at the top, on whichever tab the caller asked for.
     *
     * Client-side navigation keeps the previous page's scroll offset, so following a link from a card
     * partway down the dashboard would otherwise land mid-page. Keyed on `location.key`, which is
     * unique per history entry: the component stays mounted across navigations within this route, so
     * without this the tab a visitor last picked would outlive the visit that picked it, and a second
     * click on Class Content would be ignored.
     */
    useEffect(() => {
        setActiveTabId(requestedTabId);
        window.scrollTo({ top: 0, left: 0 });
    }, [location.key, requestedTabId]);

    /**
     * Clear the previous class's answer; this component stays mounted across navigations within the
     * route. Done during render, not in an effect: `onLoadError` fires from `componentDidCatch`, so
     * an effect would run after the new report and reset the answer it had just produced.
     */
    const reportedCourseId = useRef(id);
    if (reportedCourseId.current !== id) {
        reportedCourseId.current = id;
        setHasClassContent(undefined);
    }

    /**
     * A remote that hangs neither resolves nor throws, so nothing else fires and the whole tab strip
     * would sit behind the loading indicator.
     *
     * `id` is a dependency so each class gets its own budget. Without it, navigating away from a class
     * that never answered leaves `hasClassContent` at `undefined`; resetting it to `undefined` is a
     * no-op React bails on, so this effect would not re-run, its cleanup would not fire, and the new
     * class would inherit whatever was left of the previous class's timer.
     */
    useEffect(() => {
        if (!isIltClassroomsXpsEnabled || hasClassContent !== undefined) {
            return;
        }
        const timer = setTimeout(() => setHasClassContent(false), CLASS_CONTENT_REPORT_TIMEOUT_MS);
        return () => clearTimeout(timer);
    }, [isIltClassroomsXpsEnabled, hasClassContent, id]);

    useEffect(() => {
        if (!loading && !learningActivityLoading && learningActivityError && !hasError) {
            setHasError(true);

            // Check if the error is an authorization error
            const isAuthError = learningActivityError.graphQLErrors?.some((error) => error.message?.includes('You are not authorized'));

            const errorMessage = isAuthError
                ? 'You are not authorized to view this page.'
                : 'Something went wrong. Unable to load course information.';

            showErrorFlashbar(errorMessage);
            return;
        }

        // New temporary logic to allow request access to render
        if (!loading && !learningActivityLoading) {
            const course = courses.find((course) => course.id === id);
            if (!course) {
                if (!learningActivityData || !learningActivityData.learningActivity) {
                    navigate(PAGE_NOT_FOUND_ROUTE);
                    return;
                }
                const courseProps: Omit<CourseProps, 'isProxy'> | undefined = convertLearningActivityToCourse(
                    {
                        ...learningActivityData.learningActivity,
                        registrationCounter: learningActivityData.learningActivity.registrationCounter ?? 0,
                    },
                    user.organizationId,
                );
                if (courseProps) {
                    setCourse({
                        ...courseProps,
                        registrationStatus: RegistrationStatus.NOT_REGISTERED, // if courses doesn't contain the target course, that means we don't have registration record nor assignment record. We need to separately populate this field to show enroll button if eligible.
                        isProxy: false,
                    });
                } else {
                    navigate(PAGE_NOT_FOUND_ROUTE);
                }
            } else {
                setCourse(course);
                document.title = course.courseDetails.name;
            }
        }
    }, [
        courses,
        loading,
        learningActivityLoading,
        learningActivityError,
        hasError,
        id,
        navigate,
        learningActivityData?.learningActivity,
        learningActivityData,
    ]);

    /**
     * A render helper, not a component — hence the lowercase name and the call below rather than
     * `<Content />`. As a component it would be a fresh element type on every render of this
     * function, so React would unmount and remount the entire subtree, including the iltclassrooms
     * live component, every time a tab is selected. It holds no hooks, so inlining its output is
     * equivalent.
     */
    const renderContent = () => {
        if (hasError) {
            return null;
        }

        const tabs = [
            {
                label: 'About',
                id: 'first',
                content: <>{course && <CourseDetails course={course} />}</>,
            },
            {
                label: 'Course outline',
                id: 'second',
                content: <>{course && <CourseOutline course={course} />}</>,
            },
            ...(isBoltOnClass
                ? [
                      {
                          label: 'Cloud Quest',
                          id: 'cloud-quest',
                          content: <CloudQuestTab userId={user?.id} cqCatalogItemId={cqCatalogItemId} />,
                      },
                      {
                          label: 'Premium Content',
                          id: 'premium-content',
                          content: <PremiumContentTab userId={user?.id} learningActivityId={id} accessEndTimestamp={accessEndTimestamp} />,
                      },
                  ]
                : []),
            ...(isIltClassroomsXpsEnabled && hasClassContent
                ? [
                      {
                          label: 'Class content',
                          id: CLASS_CONTENT_TAB_ID,
                          content: (
                              <SuspenseLiveComponent
                                  experienceProviderName='iltclassrooms'
                                  componentName='ClassContentTab'
                                  props={{ learningActivityId: id }}
                              />
                          ),
                      },
                  ]
                : []),
        ];

        /**
         * Fall back to the first tab when the navigation state names a tab that is not rendered — an
         * unrecognized id, or `class-content` while the feature flag is off. Cloudscape would
         * otherwise show no tab as selected and no panel at all.
         */
        const resolvedTabId = tabs.some((tab) => tab.id === activeTabId) ? activeTabId : tabs[0].id;

        const isAwaitingClassContent = isIltClassroomsXpsEnabled && hasClassContent === undefined;

        // Nothing while the course loads; the header already shows a spinner. An indicator rather
        // than a skeleton because Cloudscape's Skeleton is not exported in awsui-components-react@3.0.0.
        const classContentLoading = course ? (
            <Box padding='l' textAlign='center'>
                <StatusIndicator type='loading'>Loading class content</StatusIndicator>
            </Box>
        ) : null;

        const courseContent = (
            <ContentLayout
                header={
                    course ? (
                        <Grid gridDefinition={[{ colspan: { s: 8, xxs: 12 } }, { colspan: { s: 4, xxs: 12 } }]}>
                            <SpaceBetween size='xxl' direction='vertical'>
                                <CourseBadges item={course} />
                                <div>
                                    <h1
                                        aria-label={course.activityName ? 'Activity Name' : 'Class Title'}
                                        style={{
                                            fontSize: '64px',
                                            fontWeight: 900,
                                            margin: 0,
                                            lineHeight: '64px',
                                        }}
                                    >
                                        {course.activityName ? course.activityName : course.courseDetails.name}
                                    </h1>
                                    {course.activityName && (
                                        <Box variant='h2' margin={{ top: 'xxs' }}>
                                            {course.courseDetails.name}
                                        </Box>
                                    )}
                                </div>
                                <SpaceBetween size='xxl' direction='horizontal' className='icons'>
                                    <DurationIcon value={calculateActualDuration(course.schedule)} size='large' />
                                    <LevelIcon value={course.courseDetails.skillLevel} size='large' />
                                    <CategoryIcon value={course.courseDetails.category} size='large' />
                                </SpaceBetween>
                            </SpaceBetween>
                            <div className='details'>
                                <div className='wrapper'>
                                    {course && (
                                        <DeliveryDetails
                                            key={id}
                                            course={course}
                                            page={TelemetryPage.COURSE_DETAIL}
                                            showLocationHeading={true}
                                            refetchCourses={refetchCourses}
                                            orgId={orgId ?? undefined}
                                            showGuideButtons={hasClassContent === false}
                                            hasClassContent={hasClassContent}
                                        />
                                    )}
                                </div>
                            </div>
                        </Grid>
                    ) : (
                        <Header>
                            <Spinner size='large' />
                            <div>Loading Course data</div>
                        </Header>
                    )
                }
            >
                <div className='content'>
                    {/* Sibling of `<Tabs>`, not inside it: it also claims the learner's Entitlement
                        Service seat, which must happen for a guides-only class that has no tab. */}
                    {isIltClassroomsXpsEnabled && (
                        <SuspenseLiveComponent
                            // Remount per class; `TabErrorBoundary` never clears `hasError`.
                            key={id}
                            experienceProviderName='iltclassrooms'
                            componentName='EnrollStudent'
                            props={{
                                learningActivityId: id,
                                registrationStatus: course?.registrationStatus,
                                onHasTabContent: reportHasClassContent,
                            }}
                            hideSuspenseFallback
                            onLoadError={() => setHasClassContent(false)}
                        />
                    )}
                    {/* The whole strip waits: inserting a tab later shifts the strip under the cursor. */}
                    {isAwaitingClassContent ? (
                        classContentLoading
                    ) : (
                        <Tabs
                            tabs={tabs}
                            activeTabId={resolvedTabId}
                            onChange={({ detail }) => {
                                setActiveTabId(detail.activeTabId);
                                let eventName;
                                switch (detail.activeTabId) {
                                    case 'first':
                                        eventName = TelemetryEvents.COURSE_DETAIL_TAB_CHANGE_ABOUT;
                                        break;
                                    case 'second':
                                        eventName = TelemetryEvents.COURSE_DETAIL_TAB_CHANGE_OUTLINE;
                                        break;
                                    case 'cloud-quest':
                                        eventName = TelemetryEvents.COURSE_DETAIL_TAB_CHANGE_CLOUD_QUEST;
                                        break;
                                    case 'premium-content':
                                        eventName = TelemetryEvents.COURSE_DETAIL_TAB_CHANGE_PREMIUM_CONTENT;
                                        break;
                                    case CLASS_CONTENT_TAB_ID:
                                        eventName = TelemetryEvents.COURSE_DETAIL_TAB_CHANGE_CLASS_CONTENT;
                                        break;
                                    default:
                                        eventName = TelemetryEvents.COURSE_DETAIL_TAB_CHANGE_ABOUT;
                                }
                                trackUserEvent(TELEMETRY_EVENT_TYPES.CLICK, eventName);
                            }}
                        />
                    )}
                </div>
            </ContentLayout>
        );

        return isChatbotEnabled ? <IltLearnerAssistantLayout>{courseContent}</IltLearnerAssistantLayout> : courseContent;
    };
    const { notifications } = useContext(NotificationContext);

    return (
        <AppLayout
            content={renderContent()}
            navigation={<Navigation />}
            contentType={'wizard'}
            notifications={<Flashbar items={notifications} />}
            toolsHide={true}
            breadcrumbs={<Breadcrumbs items={getBreadcrumbItemsFromCourse(course?.courseDetails.name ?? '')} />}
        />
    );
};
